import React, { useMemo } from 'react';

import Lottie from 'lottie-react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { deepCloneRecord } from 'utils/utils';
import { hexToLottieRgb } from 'utils/recolorLottieJson';

const recolorLottie = (data: Record<string, unknown>, rgb: [number, number, number]): Record<string, unknown> => {
  const parsed = deepCloneRecord(data);

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const obj = node as Record<string, unknown>;
    if (obj.ty === 'st' && obj.c && typeof obj.c === 'object') {
      const c = obj.c as Record<string, unknown>;
      if (c.k && Array.isArray(c.k) && c.k.length >= 3) {
        c.k = [...rgb];
      }
    }
    Object.values(obj).forEach(walk);
  };

  walk(parsed);
  return parsed;
};

const DEFAULT_CONTAINER_SIZE = 40;
const DEFAULT_ICON_SIZE = 32;

export type AnimatedHoverIconProps = {
  /** Static inline SVG component shown at rest. Strokes should use `currentColor` to inherit `color`. */
  staticIcon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  /** Optional Lottie JSON; played once on hover if `isHovered` becomes true. */
  animationData?: Record<string, unknown>;
  /** Hex color for the icon foreground (drives both `currentColor` at rest and Lottie stroke recolor). */
  color: string;
  /** Hex color for the container background tint. */
  backgroundColor: string;
  /** When true, the Lottie animation mounts and autoplays. At rest the static SVG is shown. */
  isHovered?: boolean;
  /** Container outer size in px. Default: 40. */
  size?: number;
  /** Inner icon/Lottie size in px. Default: 32. */
  iconSize?: number;
  /** Container border-radius override. Default: `theme.spacing(1.5)` (12px at 8px base). */
  borderRadius?: number | string;
};

const AnimatedHoverIcon = React.memo(function AnimatedHoverIcon({
  staticIcon: IconComponent,
  animationData,
  color,
  backgroundColor,
  isHovered = false,
  size = DEFAULT_CONTAINER_SIZE,
  iconSize = DEFAULT_ICON_SIZE,
  borderRadius,
}: AnimatedHoverIconProps) {
  const theme = useTheme();

  const coloredAnimationData = useMemo(() => {
    if (!animationData) return undefined;
    return recolorLottie(animationData, hexToLottieRgb(color));
  }, [animationData, color]);

  const containerSx = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: borderRadius ?? theme.spacing(1.5),
    backgroundColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color,
  };

  if (isHovered && coloredAnimationData) {
    return (
      <Box sx={containerSx}>
        <Lottie
          animationData={coloredAnimationData}
          loop={false}
          autoplay
          style={{ width: iconSize, height: iconSize }}
        />
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <IconComponent width={iconSize} height={iconSize} style={{ display: 'block' }} />
    </Box>
  );
});

export default AnimatedHoverIcon;
